import gate from "../gate";
import client from "../http-clients/api";
import {
  ICourseParams,
  ICourseCommentData,
  IPostCommentInput,
  ICourseCommentsResult,
  ICourseQuestionResult,
  ICourseQuestionData,
  StepWatchTimeInput,
  CreateNoteInput,
  ICourseNotesResult,
  PaginationData,
  CreateNoteOutput,
  CreateQAInput,
  CreateQAOutput,
  CoursePostCommentOutput,
  RateOutput,
  EditQAInput,
  ToggleFavOutput,
  DeleteQuestionOutput,
  ICourse,
  ICourseCard,
  ICourseLikeDislikeQuestionResult,
  IPreviewPackCourse,
  SORT_NOTE,
  GuestStepWatchTimeInput,
} from "@types";

const courseApi = {
  getCourseData: (slug: string, params?: ICourseParams) =>
    gate.get<ICourse, never, ICourseParams>({
      client,
      url: `/product/${slug}/show`,
      params,
    }),
  checkRedirectProduct: (slug: string) =>
    gate.get<{ status: boolean }, never, never>({
      client,
      url: `/product/${slug}/redirect`,
    }),
  getCourseCard: (slug: string) =>
    gate.get<ICourseCard, never, never>({
      client,
      url: `/product/${slug}/cards`,
    }),
  getComments: (id: string, data?: ICourseCommentData) =>
    gate.get<ICourseCommentsResult, ICourseCommentData, never>({
      client,
      url: `/product/${id}/comments`,
      data,
    }),
  postComment: (data: IPostCommentInput) =>
    gate.post<CoursePostCommentOutput, IPostCommentInput, never>({
      client,
      url: `/product/comment`,
      data,
    }),
  postVideoCourseRegister: (product_id: number) =>
    gate.post<CoursePostCommentOutput, never, never>({
      client,
      url: `/order/register/${product_id}`,
    }),
  getQuestions: (id: number, data?: ICourseQuestionData) =>
    gate.get<ICourseQuestionResult, ICourseQuestionData, never>({
      client,
      url: `/product/${id}/question?showHtml=1`,
      data,
    }),
  likeQa: (product_id: number, question_id: number) =>
    gate.post<ICourseLikeDislikeQuestionResult, never, never>({
      client,
      url: `/product/${product_id}/question/${question_id}/like`,
    }),
  dislikeQa: (product_id: number, question_id: number) =>
    gate.delete<ICourseLikeDislikeQuestionResult, never, never>({
      client,
      url: `/product/${product_id}/question/${question_id}/like`,
    }),
  addQuestion: (id: number, data: CreateQAInput) =>
    gate.post<CreateQAOutput, CreateQAInput, never>({
      client,
      url: `/product/${id}/question`,
      data,
    }),
  editQuestion: (product_id: number, question_id: number, data: EditQAInput) =>
    gate.patch<any, EditQAInput, never>({
      client,
      url: `/product/${product_id}/question/${question_id}`,
      data,
    }),
  deleteQuestion: (product_id: number, question_id: number) =>
    gate.delete<DeleteQuestionOutput, never, never>({
      client,
      url: `/product/${product_id}/question/${question_id}`,
    }),
  restoreQuestion: (question_id: number) =>
    gate.get<DeleteQuestionOutput, never, never>({
      client,
      url: `/product/question/${question_id}/restore`,
    }),
  toggleFav: (id: number) =>
    gate.post<ToggleFavOutput, never, never>({
      client,
      url: `/product/${id}/favorite`,
    }),

  updateWatchTime: (data: StepWatchTimeInput) =>
    gate.post<void, StepWatchTimeInput, never>({
      client,
      url: `/product/lesson/watch`,
      data,
    }),
  updateGuestWatchTime: (data: GuestStepWatchTimeInput) =>
    gate.post<void, StepWatchTimeInput, never>({
      client,
      url: `/product/lesson/guest/watch`,
      data,
    }),

  createNote: (data: CreateNoteInput) => {
    return gate.post<CreateNoteOutput, CreateNoteInput, never>({
      client,
      url: "/user/notes/store",
      data,
    });
  },
  updateNote: (id: number, note: string) =>
    gate.patch<any, { id: number; note: string }, never>({
      client,
      url: `/user/notes/update`,
      data: {
        id,
        note,
      },
    }),
  deleteNote: (id: number) =>
    gate.delete<any, { id: number }, never>({
      client,
      url: `/user/notes/destroy`,
      data: {
        id,
      },
    }),
  restoreNote: (id: number) =>
    gate.post<any, { id: number }, never>({
      client,
      url: `/user/notes/restore`,
      data: {
        id,
      },
    }),
  lessonNotes: (
    id: number,
    lessonId: number,
    data: PaginationData = { page: 1, limit: 1000 },
    sort: SORT_NOTE
  ) =>
    gate.get<ICourseNotesResult, PaginationData, { sort: SORT_NOTE }>({
      client,
      url: `/product/${id}/lessons/${lessonId}/notes`,
      data,
      params: { sort: sort },
    }),
  productNotes: (id: number, data: PaginationData = { page: 1, limit: 1000 }) =>
    gate.get<ICourseNotesResult, PaginationData>({
      client,
      url: `/product/${id}/notes`,
      data,
    }),
  productRate: (id: number, data: { rate: 1 | 2 | 3 | 4 | 5 | null }) =>
    gate.post<RateOutput, { rate: 1 | 2 | 3 | 4 | 5 | null }, never>({
      client,
      url: `/product/${id}/rate`,
      data,
    }),
  report: (id: string) =>
    gate.get<string>({
      client,
      url: `https://faradars.org/courses/${id}/details`,
    }),
  packPreviewProduct: (id: Number) =>
    gate.get<IPreviewPackCourse, never, never>({
      client,
      url: `/product/${id}/preview`,
    }),
};

export default courseApi;
